import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';
import { DashboardService } from '../../services/dashboard.service';
import { StockService } from '../../services/stock.service';
import { RequisitionService } from '../../services/requisition.service';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { FulfilmentService } from '../../services/fulfilment.service';
import { SalesReturnService } from '../../services/salesReturn.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { StoreAdminDashboard as StoreAdminDashboardData } from '../../interfaces/dashboard.interface';
import { Stock } from '../model/stock';
import { Store } from '../../interfaces/store.interface';
import { Requisition } from '../../interfaces/requisition.interface';
import { PurchaseOrder } from '../../interfaces/procurement/purchaseOrder.interface';
import { ShipmentRow } from '../../interfaces/sales/fulfilment.interface';
import { SalesReturnSummary } from '../../interfaces/sales/salesReturn.interface';
import { formatDate } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/format';
import StatTile from './charts/StatTile';
import HBars from './charts/HBars';
import { compactMoney } from './charts/axisPadding';
import MyBreakdownsDashSection from '../maintenance/MyBreakdownsDashSection';
import MyWidgetsSection from './board/MyWidgetsSection';

const STOCK_PAGE_SIZE = 200;
const LIST_LIMIT = 5;
const TOP_N = 8;

const fmtInt = (n: number) => Math.round(n).toLocaleString('en-US');

interface StoreStock { store: Store; stocks: Stock[]; totalLines: number; }

function Section({ title, subtitle, children, ...rest }: { title: string; subtitle?: string; children: React.ReactNode; 'data-help-section'?: string }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }} {...rest}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
            {subtitle && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{subtitle}</Typography>}
            <Box sx={{ mt: subtitle ? 0 : 1 }}>{children}</Box>
        </Paper>
    );
}

function Empty({ text }: { text: string }) {
    return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>{text}</Typography>;
}

function RequisitionList({ rows, basePath, testId }: { rows: Requisition[]; basePath: string; testId: string }) {
    return (
        <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
            {rows.slice(0, LIST_LIMIT).map(r => (
                <Stack key={r.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap data-testid={`${testId}-${r.id}`}>
                    <Link component={RouterLink} to={`${basePath}/${r.id}`} underline="hover" sx={{ fontWeight: 600 }}>
                        {r.refNo || 'Requisition'}
                    </Link>
                    <Typography variant="body2" color="text.secondary">{r.requisitionByName}</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        {/* The list endpoints may omit line details; only state a count we actually have. */}
                        {[
                            r.details?.length ? `${r.details.length} item${r.details.length === 1 ? '' : 's'}` : null,
                            r.date ? formatDate(r.date) : null,
                        ].filter(Boolean).join(' · ')}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    );
}

/**
 * Store dashboard — KPI tiles, charts and action lists. The summary endpoint (store-admin
 * dashboard) carries the headline numbers; every other block comes from an endpoint the caller's
 * store role can already reach and is fetched independently, so a failure (or a missing
 * permission) only hides the widgets that depend on it.
 */
const StoreAdminDashboard: React.FC = () => {
    const canManageStock = usePermission(PERMISSIONS.STOCK_MANAGE);
    const canViewStock = usePermission(PERMISSIONS.STOCK_VIEW);
    const canIssueReq = usePermission(PERMISSIONS.REQUISITION_ISSUE);
    const canApproveReq = usePermission(PERMISSIONS.REQUISITION_APPROVE);
    const canStockOps = usePermission(PERMISSIONS.STOCK_ISSUE);
    const canMenuStores = usePermission(PERMISSIONS.MENU_STORES);
    const canStocktake = usePermission(PERMISSIONS.STOCK_MANAGE);
    const canTransfer = usePermission(PERMISSIONS.STOCK_MANAGE);
    const canProcure = usePermission(PERMISSIONS.PURCHASE_ORDER_VIEW);
    // Goods receipts is a procurement list; offer it only to the store role that books receipts.
    const canReceiveGoods = usePermission(PERMISSIONS.GOODS_RECEIPT_MANAGE);
    // Store-side tasks in procurement and sales, shown here as queues so store roles need no
    // Procurement / Sales menu: the Store Manager PO approval band, shipping posted challans,
    // and receiving customer returns into stock.
    const canApprovePo = usePermission(PERMISSIONS.PURCHASE_ORDER_APPROVE) && canProcure;
    const canViewSalesOrders = usePermission(PERMISSIONS.SALES_ORDER_VIEW);
    const canShip = usePermission(PERMISSIONS.SHIPMENT_MANAGE) && canViewSalesOrders;
    const canReceiveReturns = usePermission(PERMISSIONS.SALES_RETURN_MANAGE) && canViewSalesOrders;

    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState<StoreAdminDashboardData | null>(null);
    const [summaryError, setSummaryError] = useState<string | null>(null);
    const [storeStocks, setStoreStocks] = useState<StoreStock[] | null>(null);
    const [lowStock, setLowStock] = useState<Stock[] | null>(null);
    const [awaitingApproval, setAwaitingApproval] = useState<Requisition[] | null>(null);
    const [awaitingIssue, setAwaitingIssue] = useState<Requisition[] | null>(null);
    const [poApprovals, setPoApprovals] = useState<{ rows: PurchaseOrder[]; total: number } | null>(null);
    const [toShip, setToShip] = useState<{ rows: ShipmentRow[]; total: number } | null>(null);
    const [returnsToReceive, setReturnsToReceive] = useState<SalesReturnSummary[] | null>(null);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) { setLoading(false); return; }
        setLoading(true);
        const loadStocks = async (): Promise<StoreStock[]> => {
            const stores = await getStoresByInstitutionId(0, STOCK_PAGE_SIZE, 'ALL');
            const pages = await Promise.allSettled(
                stores.content.map(s => StockService.getStockByStore(s.id as string, 0, STOCK_PAGE_SIZE)),
            );
            const out: StoreStock[] = [];
            pages.forEach((p, i) => {
                if (p.status === 'fulfilled') {
                    out.push({ store: stores.content[i], stocks: p.value.content, totalLines: p.value.totalElements });
                }
            });
            if (stores.content.length > 0 && out.length === 0) throw new Error('No store stock could be loaded');
            return out;
        };
        const [sum, stocks, low, appr, iss] = await Promise.allSettled([
            DashboardService.getStoreAdminDashboard(institutionId),
            canViewStock ? loadStocks() : Promise.reject(new Error('skipped')),
            canManageStock ? StockService.getLowStock(institutionId) : Promise.reject(new Error('skipped')),
            (canManageStock || canApproveReq) ? RequisitionService.getPendingApprovalRequisitionsByInstitution(0, LIST_LIMIT) : Promise.reject(new Error('skipped')),
            canIssueReq ? RequisitionService.getPendingIssueRequisitionsByInstitution(0, LIST_LIMIT) : Promise.reject(new Error('skipped')),
        ]);
        const [po, ship, ret] = await Promise.allSettled([
            canApprovePo ? PurchaseOrderService.listByInstitution(0, LIST_LIMIT, 'PENDING_APPROVAL') : Promise.reject(new Error('skipped')),
            canShip ? FulfilmentService.list(institutionId, { status: 'PENDING', page: 0, size: LIST_LIMIT }) : Promise.reject(new Error('skipped')),
            canReceiveReturns ? SalesReturnService.list(institutionId, { status: 'DRAFT' }) : Promise.reject(new Error('skipped')),
        ]);
        setPoApprovals(po.status === 'fulfilled' ? { rows: po.value.content, total: po.value.totalElements } : null);
        setToShip(ship.status === 'fulfilled' ? { rows: ship.value.content, total: ship.value.totalElements } : null);
        setReturnsToReceive(ret.status === 'fulfilled' ? ret.value : null);
        if (sum.status === 'fulfilled') { setSummary(sum.value); setSummaryError(null); }
        else {
            setSummary(null);
            setSummaryError((sum.reason as any)?.response?.data?.message || 'Failed to load dashboard data');
        }
        setStoreStocks(stocks.status === 'fulfilled' ? stocks.value : null);
        setLowStock(low.status === 'fulfilled' ? low.value : null);
        setAwaitingApproval(appr.status === 'fulfilled' ? appr.value.content : null);
        setAwaitingIssue(iss.status === 'fulfilled' ? iss.value.content : null);
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canViewStock, canManageStock, canApproveReq, canIssueReq, canApprovePo, canShip, canReceiveReturns]);

    useEffect(() => { load(); }, [load]);

    const stockStats = useMemo(() => {
        if (!storeStocks) return null;
        const byStore = storeStocks.map(ss => {
            const qty = ss.stocks.reduce((s, x) => s + (x.availableQty ?? 0), 0);
            const value = ss.stocks.reduce((s, x) => s + (x.totalValue ?? 0), 0);
            return { name: ss.store.storeName, qty, value, lines: ss.totalLines };
        });
        const truncated = storeStocks.some(ss => ss.totalLines > ss.stocks.length);
        const totalValue = byStore.reduce((s, x) => s + x.value, 0);
        const byItem = new Map<string, { name: string; qty: number; value: number }>();
        storeStocks.forEach(ss => ss.stocks.forEach(x => {
            const cur = byItem.get(x.itemId) ?? { name: x.item?.itemName ?? 'Unknown item', qty: 0, value: 0 };
            byItem.set(x.itemId, { ...cur, qty: cur.qty + (x.availableQty ?? 0), value: cur.value + (x.totalValue ?? 0) });
        }));
        const items = Array.from(byItem.values());
        const topQty = items.filter(i => i.qty > 0).sort((a, b) => b.qty - a.qty).slice(0, TOP_N)
            .map(i => ({ label: i.name, value: i.qty }));
        const topValue = items.filter(i => i.value > 0).sort((a, b) => b.value - a.value).slice(0, TOP_N)
            .map(i => ({ label: i.name, value: i.value }));
        const storeQty = byStore.filter(s => s.qty > 0).sort((a, b) => b.qty - a.qty).slice(0, TOP_N)
            .map(s => ({
                label: s.name, value: s.qty,
                tooltip: [{ label: 'Quantity', value: fmtInt(s.qty) }, { label: 'Item lines', value: fmtInt(s.lines) }, { label: 'Value', value: formatMoney(s.value) }],
            }));
        return { truncated, totalValue, topQty, topValue, storeQty, hasValue: totalValue > 0 };
    }, [storeStocks]);

    // Low-stock rows: prefer the reorder-level endpoint, fall back to the summary's own list.
    const lowRows = useMemo(() => {
        if (lowStock) {
            return lowStock.map(s => ({
                key: s.id ?? `${s.storeId}-${s.itemId}`,
                store: s.store?.storeName ?? '', item: s.item?.itemName ?? 'Unknown item',
                qty: s.availableQty, reorder: s.reorderLevel as number | undefined,
            }));
        }
        if (summary) {
            return summary.lowStockItems.map((l, i) => ({
                key: `${l.storeName}-${l.itemName}-${i}`, store: l.storeName, item: l.itemName,
                qty: l.availableQty, reorder: undefined as number | undefined,
            }));
        }
        return null;
    }, [lowStock, summary]);

    const pipeline = useMemo(() => {
        if (!summary) return [];
        return [
            { label: 'Awaiting approval', value: summary.pendingApprovalRequisitions },
            // Only offered to holders of the permission the issue queue endpoint requires.
            ...(canIssueReq ? [{ label: 'Awaiting issue', value: summary.pendingIssueRequisitions }] : []),
        ].filter(d => d.value > 0);
    }, [summary, canIssueReq]);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
    }

    const noData = !!summary && summary.totalStores === 0;
    const showLowStock = !!lowRows;

    return (
        <Box data-help-page="store.dash.page">
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight="bold">Store Dashboard</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap data-help-section="store.dash.shortcuts">
                    {canMenuStores && <Button component={RouterLink} to="/stocks" size="small" variant="contained">Stock list</Button>}
                    {(canApproveReq || canIssueReq) && (
                        <Button component={RouterLink} to={canApproveReq ? '/pending-approval-requisitions' : '/pending-issue-requisitions'} size="small" variant="outlined">
                            {canApproveReq ? 'Requisitions to approve' : 'Requisitions to issue'}
                        </Button>
                    )}
                    {canApproveReq && canIssueReq && (
                        <Button component={RouterLink} to="/pending-issue-requisitions" size="small" variant="outlined">Requisitions to issue</Button>
                    )}
                    {canMenuStores && canStockOps && <Button component={RouterLink} to="/stock-in" size="small" variant="outlined">Stock in</Button>}
                    {canMenuStores && canStockOps && <Button component={RouterLink} to="/stock-out" size="small" variant="outlined">Stock out</Button>}
                    {canTransfer && <Button component={RouterLink} to="/stock-transfers" size="small" variant="outlined">Transfers</Button>}
                    {canStocktake && <Button component={RouterLink} to="/stocktakes" size="small" variant="outlined">Stocktakes</Button>}
                    {canProcure && canReceiveGoods && <Button component={RouterLink} to="/procurement/goods-receipts" size="small" variant="outlined">Goods receipts</Button>}
                </Stack>
            </Stack>

            {summaryError && <Alert severity="error" sx={{ mb: 2 }} data-help-section="store.dash.notice">{summaryError}</Alert>}
            {stockStats?.truncated && (
                <Alert severity="info" sx={{ mb: 2 }} data-help-section="store.dash.notice">
                    Some stores hold more than {STOCK_PAGE_SIZE} item lines; the charts below use the first {STOCK_PAGE_SIZE} lines per store.
                </Alert>
            )}

            {noData && (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', mb: 2 }} data-testid="store-dashboard-empty" data-help-section="store.dash.empty">
                    <Typography variant="h6" gutterBottom>No stores yet</Typography>
                    <Typography color="text.secondary">Create a store and stock in some items to see stock levels and movements here.</Typography>
                </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 1, mb: 2 }} data-help-section="store.dash.kpis">
                <Stack direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {summary && (
                        <>
                            <StatTile data-help="store.dash.tile.stores" label="Stores" value={fmtInt(summary.totalStores)} />
                            <StatTile data-help="store.dash.tile.items" label="Items" value={fmtInt(summary.totalUniqueItems)} sub="Distinct items stocked" />
                            <StatTile data-help="store.dash.tile.onHand" label="Stock on hand" value={fmtInt(summary.totalStockQuantity)} sub="Total quantity, all stores" />
                            {stockStats?.hasValue && <StatTile data-help="store.dash.tile.value" label="Stock value" unit="৳" value={formatMoney(stockStats.totalValue)} sub={stockStats.truncated ? 'First 200 lines per store' : undefined} />}
                            <StatTile data-help="store.dash.tile.inToday" label="Stock in today" value={fmtInt(summary.todayStockIn)} sub="Incl. transfers and stocktake gains" />
                            <StatTile data-help="store.dash.tile.outToday" label="Stock out today" value={fmtInt(summary.todayStockOut)} sub="Incl. transfers and issues" />
                            <StatTile data-help="store.dash.tile.lowStock" label="Low-stock items" value={fmtInt(summary.lowStockItemCount)} tone={summary.lowStockItemCount > 0 ? 'warning' : 'good'} sub="At or below reorder level" />
                            <StatTile data-help="store.dash.tile.awaitingApproval" label="Awaiting approval" value={fmtInt(summary.pendingApprovalRequisitions)} tone={summary.pendingApprovalRequisitions > 0 ? 'warning' : 'default'} sub="Requisitions" />
                            {canIssueReq && <StatTile data-help="store.dash.tile.awaitingIssue" label="Awaiting issue" value={fmtInt(summary.pendingIssueRequisitions)} sub="Approved requisitions" />}
                        </>
                    )}
                    {!summary && <Typography color="text.secondary" sx={{ p: 1.5 }}>Figures unavailable.</Typography>}
                </Stack>
            </Paper>

            {!noData && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'repeat(2, minmax(0,1fr))' }, gap: 2, mb: 2 }}>
                    {stockStats && (
                        <>
                            <Section data-help-section="store.dash.stockByStore" title="Stock by store" subtitle="Quantity on hand per store">
                                {stockStats.storeQty.length === 0 ? <Empty text="No stock on hand." /> : <HBars data={stockStats.storeQty} vFmt={fmtInt} />}
                            </Section>
                            <Section data-help-section="store.dash.topQty" title="Top items by quantity" subtitle="Across all stores">
                                {stockStats.topQty.length === 0 ? <Empty text="No stock on hand." /> : <HBars data={stockStats.topQty} vFmt={fmtInt} />}
                            </Section>
                            {stockStats.hasValue && (
                                <Section data-help-section="store.dash.topValue" title="Top items by value" subtitle="Stock value across all stores">
                                    <HBars data={stockStats.topValue} vFmt={compactMoney} />
                                </Section>
                            )}
                        </>
                    )}
                    {summary && (
                        <Section data-help-section="store.dash.pipeline" title="Requisition pipeline" subtitle="Open requisitions by stage">
                            {pipeline.length === 0 ? <Empty text="No open requisitions." /> : <HBars data={pipeline} colorIndex={1} />}
                        </Section>
                    )}
                    {awaitingApproval && (
                        <Section data-help-section="store.dash.awaitingApproval" title="Awaiting approval" subtitle="Requisitions waiting for a decision">
                            {awaitingApproval.length === 0 ? <Empty text="Nothing waiting for approval." />
                                : <RequisitionList rows={awaitingApproval} basePath="/pending-approval-requisitions" testId="approval" />}
                        </Section>
                    )}
                    {awaitingIssue && (
                        <Section data-help-section="store.dash.awaitingIssue" title="Awaiting issue" subtitle="Approved, ready to hand over">
                            {awaitingIssue.length === 0 ? <Empty text="Nothing waiting to be issued." />
                                : <RequisitionList rows={awaitingIssue} basePath="/pending-issue-requisitions" testId="issue" />}
                        </Section>
                    )}
                    {poApprovals && (
                        <Section data-help-section="store.dash.poApprove" title="Purchase orders to approve" subtitle={`${fmtInt(poApprovals.total)} waiting for approval`}>
                            {poApprovals.rows.length === 0 ? <Empty text="No purchase orders waiting." /> : (
                                <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                                    {poApprovals.rows.map(po => (
                                        <Stack key={po.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap data-testid={`po-approval-${po.id}`}>
                                            <Link component={RouterLink} to={`/procurement/purchase-orders/pending-approval/${po.id}`} underline="hover" sx={{ fontWeight: 600 }}>
                                                {po.poNumber}
                                            </Link>
                                            <Typography variant="body2" color="text.secondary">{po.supplierName}</Typography>
                                            <Box sx={{ flex: 1 }} />
                                            <Typography variant="body2" color="text.secondary">{po.orderDate ? formatDate(po.orderDate) : ''}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                        </Section>
                    )}
                    {toShip && (
                        <Section data-help-section="store.dash.shipments" title="Shipments to send" subtitle={`${fmtInt(toShip.total)} packed or waiting to be shipped`}>
                            {toShip.rows.length === 0 ? <Empty text="Nothing waiting to ship." /> : (
                                <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                                    {toShip.rows.map(r => (
                                        <Stack key={r.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap data-testid={`to-ship-${r.id}`}>
                                            <Link component={RouterLink} to={`/sales/challans/${r.challanId}?tab=fulfilment`} underline="hover" sx={{ fontWeight: 600 }}>
                                                {r.challanNo || r.shipmentNo}
                                            </Link>
                                            <Typography variant="body2" color="text.secondary">{r.customerName}</Typography>
                                            <Box sx={{ flex: 1 }} />
                                            <Typography variant="body2" color="text.secondary">{fmtInt(r.packageCount)} package{r.packageCount === 1 ? '' : 's'}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                        </Section>
                    )}
                    {returnsToReceive && (
                        <Section data-help-section="store.dash.returns" title="Customer returns to receive" subtitle={`${fmtInt(returnsToReceive.length)} draft returns waiting to be posted into stock`}>
                            {returnsToReceive.length === 0 ? <Empty text="No returns waiting." /> : (
                                <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                                    {returnsToReceive.slice(0, LIST_LIMIT).map(r => (
                                        <Stack key={r.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap data-testid={`return-${r.id}`}>
                                            <Link component={RouterLink} to={`/sales/returns/${r.id}`} underline="hover" sx={{ fontWeight: 600 }}>
                                                {r.returnNo || 'Return'}
                                            </Link>
                                            <Typography variant="body2" color="text.secondary">{r.customerName}</Typography>
                                            <Box sx={{ flex: 1 }} />
                                            <Typography variant="body2" color="text.secondary">{r.returnDate ? formatDate(r.returnDate) : ''}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                        </Section>
                    )}
                    {showLowStock && (
                        <Section data-help-section="store.dash.lowStock" title="Low stock" subtitle="Items at or below their reorder level">
                            {lowRows!.length === 0 ? <Empty text="Nothing is running low." /> : (
                                <Stack spacing={1} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                                    {lowRows!.slice(0, LIST_LIMIT).map(r => (
                                        <Stack key={r.key} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap data-testid="low-stock-row">
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.item}</Typography>
                                            <Typography variant="body2" color="text.secondary">{r.store}</Typography>
                                            <Box sx={{ flex: 1 }} />
                                            <Typography variant="body2" color={r.qty === 0 ? 'error' : 'text.primary'} sx={{ fontWeight: r.qty === 0 ? 700 : 400 }}>
                                                {fmtInt(r.qty)}{r.reorder != null ? ` / ${fmtInt(r.reorder)}` : ''}
                                            </Typography>
                                        </Stack>
                                    ))}
                                    {lowRows!.length > LIST_LIMIT && canMenuStores && (
                                        <Link component={RouterLink} to="/low-stock" underline="hover" variant="body2">
                                            View all {fmtInt(lowRows!.length)} low-stock items
                                        </Link>
                                    )}
                                </Stack>
                            )}
                        </Section>
                    )}
                </Box>
            )}
            <MyBreakdownsDashSection />
            <MyWidgetsSection boardKey="store" />
        </Box>
    );
};

export default StoreAdminDashboard;
